import * as Placeholder from '-/Custom/Placeholder';
import { Link, Stack, useLocalSearchParams } from 'expo-router';
import { useQuery } from 'urql';
import Paginator from '@/components/Custom/Paginator';
import SearchBar from '@/components/Custom/SearchBar';
import * as PersonListItem from '@/components/Person/PersonListItem';
import Box from '@/components/UI/Box';
import Item from '@/components/UI/PressableRow';
import List from '@/components/Unistyles/List';
import { type FragmentOf, graphql } from '@/gql/graphql';
import type { PersonBoolExp } from '@/gql/scalars';
import useDebounce from '@/hooks/useDebounce';
import useSearchablePaginatedQuery from '@/hooks/useSearchablePaginatedQuery';
import useUserId from '@/hooks/useUserId';
import { DEFAULT_SEARCH_MINIMUM } from '@/utils/constants';

const GetTeamTransfer = graphql(/* GraphQL */ `
  query GetTeamTransfer($teamId: uuid!) {
    team_by_pk(id: $teamId) {
      id
      title
    }
  }
`);

const GetTransferPeople = graphql(/* GraphQL */ `
  query GetTeamTransferPeople(
    $where: person_bool_exp!
    $limit: Int!
    $offset: Int!
  ) {
    person(
      where: $where
      limit: $limit
      offset: $offset
      order_by: { name: asc }
    ) {
      id
      ...PersonListItemAvatar
      ...PersonListItemName
    }
    person_aggregate(where: $where) {
      aggregate {
        count
      }
    }
  }
`, [
  PersonListItem.PersonListItemAvatarFragment,
  PersonListItem.PersonListItemNameFragment,
]);

const SearchTransferPeople = graphql(/* GraphQL */ `
  query SearchTeamTransferPeople(
    $where: person_bool_exp!
    $limit: Int!
    $offset: Int!
  ) {
    person(
      where: $where
      limit: $limit
      offset: $offset
      order_by: { name: asc }
    ) {
      id
      ...PersonListItemAvatar
      ...PersonListItemName
    }
  }
`, [
  PersonListItem.PersonListItemAvatarFragment,
  PersonListItem.PersonListItemNameFragment,
]);

export default function TeamTransfer() {
  const userId = useUserId();

  const { teamId } = useLocalSearchParams<{
    teamId?: string;
  }>();

  const [{ data, fetching }] = useQuery({
    query: GetTeamTransfer,
    variables: { teamId: teamId! },
  });

  const team = data?.team_by_pk;

  const { value, debouncedValue, onChangeText, reset } = useDebounce(
    DEFAULT_SEARCH_MINIMUM,
  );

  const baseWhere: PersonBoolExp = {
    team_transfer_recipient: { team_id: { _eq: teamId } },
    id: { _neq: userId },
  };

  const searchWhere: PersonBoolExp = {
    _and: [
      baseWhere,
      {
        name: {
          _ilike: `%${debouncedValue}%`,
        },
      },
    ],
  };

  const {
    items,
    total,
    fetching: fetchingPeople,
    pagination,
  } = useSearchablePaginatedQuery({
    searchTerm: debouncedValue,
    searchOnMinimum: DEFAULT_SEARCH_MINIMUM,
    pageSize: 25,
    getQuery: GetTransferPeople,
    getVariables: ({ limit, offset }) => ({
      where: baseWhere,
      limit,
      offset,
    }),
    selectGetItems: (d) => d?.person ?? [],
    selectGetTotal: (d) => d?.person_aggregate.aggregate?.count ?? 0,
    searchQuery: SearchTransferPeople,
    searchVariables: ({ searchTerm }) => ({
      where:
        searchTerm.length >= DEFAULT_SEARCH_MINIMUM ? searchWhere : baseWhere,
      limit: 25,
      offset: 0,
    }),
    selectSearchItems: (d) => d?.person ?? [],
  });

  type TransferItem = {
    id: string;
  } & FragmentOf<typeof PersonListItem.PersonListItemAvatarFragment> &
    FragmentOf<typeof PersonListItem.PersonListItemNameFragment>;

  return (
    <Box flex>
      <Stack.Screen
        options={{ title: team?.title ? `Transfer ${team?.title}` : '' }}
      />
      <List
        data={items}
        fetching={fetching || fetchingPeople}
        keyExtractor={(item: TransferItem) => item.id}
        ListHeaderComponent={
          <Box px="xl" py="md">
            <SearchBar
              value={value}
              onChangeText={onChangeText}
              onPressCancel={reset}
              placeholder="Search Members..."
            />
          </Box>
        }
        ListFooterComponent={
          typeof total === 'number' ? (
            <Paginator {...pagination} total={total} />
          ) : null
        }
        renderItem={({ item }) => {
          return (
            <Link
              href={{
                pathname: '/team/[teamId]/edit/transfer/[transferId]',
                params: {
                  teamId,
                  transferId: item.id,
                },
              }}
              asChild
            >
              <Item>
                <PersonListItem.Container>
                  <PersonListItem.Avatar fragment={item} />
                  <PersonListItem.Content>
                    <PersonListItem.Name fragment={item} />
                  </PersonListItem.Content>
                </PersonListItem.Container>
              </Item>
            </Link>
          );
        }}
        ListEmptyComponent={
          <Placeholder.Container>
            <Placeholder.Icon name="people-outline" />
            <Placeholder.Text>No Members To Transfer To</Placeholder.Text>
          </Placeholder.Container>
        }
      />
    </Box>
  );
}
